import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  FileText,
  TrendingUp,
  Building2,
  CheckCircle,
  AlertCircle,
  Download,
  Receipt,
  Percent,
  Printer,
  BookOpen,
  ListOrdered,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { downloadXlsx, dateStamp } from '@/lib/download-xlsx';
import { showErrorToast } from '@/hooks/use-mutation-with-toast';
import { WithholdingReport } from '@/components/accounting/withholding-report';
import { TradingKdvReport } from '@/components/accounting/trading-kdv-report';
import { LedgerBookReport } from '@/components/accounting/ledger-book-report';
import { JournalBookReport } from '@/components/accounting/journal-book-report';
import { ReportExportButton } from '@/components/accounting/report-export-button';
import {
  MizanPeriodPicker,
  defaultMizanPeriod,
  mizanPeriodToDates,
} from '@/components/reports/mizan-period-picker';

type ReportTab = 'trial-balance' | 'profit-loss' | 'balance-sheet' | 'kdv' | 'withholding' | 'ledger' | 'journal';

// TDMS Account Type Labels
const TDMS_TYPE_LABELS: Record<string, string> = {
  CURRENT_ASSET: 'Donen Varliklar',
  FIXED_ASSET: 'Duran Varliklar',
  SHORT_TERM_LIABILITY: 'Kisa Vadeli Yab. Kaynaklar',
  LONG_TERM_LIABILITY: 'Uzun Vadeli Yab. Kaynaklar',
  EQUITY: 'Oz Kaynaklar',
  INCOME: 'Gelirler',
  COST: 'Giderler/Maliyetler',
  FREE: 'Serbest',
  OFF_BALANCE: 'Nazim Hesaplar',
};

const TABS: { id: ReportTab; label: string; icon: React.ElementType }[] = [
  { id: 'trial-balance', label: 'Trial Balance (Mizan)', icon: FileText },
  { id: 'profit-loss', label: 'Profit & Loss (Gelir Tablosu)', icon: TrendingUp },
  { id: 'balance-sheet', label: 'Balance Sheet (Bilanco)', icon: Building2 },
  { id: 'kdv', label: 'KDV Beyanname Özeti', icon: Receipt },
  { id: 'withholding', label: 'Stopaj / Withholding', icon: Percent },
  { id: 'ledger', label: 'Defter-i Kebir', icon: BookOpen },
  { id: 'journal', label: 'Yevmiye Defteri', icon: ListOrdered },
];

export function TradingReportsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('tradingAccounting');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as ReportTab) || 'trial-balance';

  const setActiveTab = (tab: ReportTab) => {
    setSearchParams({ tab });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/trading/accounting')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{t('reports.title', 'Financial Reports')}</h1>
          <p className="text-muted-foreground">
            {t('reports.subtitle', 'View and export TDMS financial reports')}
          </p>
        </div>
        <Button variant="outline" className="ml-auto h-8 print:hidden" onClick={() => window.print()}>
          <Printer className="mr-2 h-4 w-4" />
          {t('reports.print', 'Yazdır / Print')}
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b print:hidden">
        <nav className="flex gap-4 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Report Content */}
      {activeTab === 'trial-balance' && <TrialBalanceReport />}
      {activeTab === 'profit-loss' && <ProfitLossReport />}
      {activeTab === 'balance-sheet' && <BalanceSheetReport />}
      {activeTab === 'kdv' && <TradingKdvReport />}
      {activeTab === 'withholding' && <WithholdingReport app="TRADING" />}
      {activeTab === 'ledger' && <LedgerBookReport app="TRADING" />}
      {activeTab === 'journal' && <JournalBookReport app="TRADING" />}
    </div>
  );
}

// Trial Balance Report (Mizan) — legacy-style four-column layout:
// period turnover (Borç/Alacak) + closing balance (Borç Bakiye/Alacak Bakiye).
function TrialBalanceReport() {
  const { t, i18n } = useTranslation('tradingAccounting');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const [period, setPeriod] = useState(defaultMizanPeriod());
  const [includeZero, setIncludeZero] = useState(false);
  const [codeFrom, setCodeFrom] = useState('');
  const [codeTo, setCodeTo] = useState('');
  const [level, setLevel] = useState(''); // '' = full detail (ayrıntılı)
  const [exporting, setExporting] = useState(false);

  const { start, end } = useMemo(() => mizanPeriodToDates(period), [period]);

  const queryInput = {
    startDate: start,
    endDate: end,
    includeZeroBalances: includeZero,
    accountCodeFrom: codeFrom.trim() || undefined,
    accountCodeTo: codeTo.trim() || undefined,
    level: level ? Number(level) : undefined,
  };

  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.tradingFinancialReports.getTrialBalance.useQuery(queryInput);

  const onExport = async () => {
    setExporting(true);
    try {
      const res = await utils.tradingFinancialReports.exportTrialBalance.fetch(queryInput);
      downloadXlsx(res.rows, 'Mizan', `mizan-${dateStamp()}.xlsx`);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const typeColors: Record<string, string> = {
    CURRENT_ASSET: 'text-blue-600',
    FIXED_ASSET: 'text-blue-700',
    SHORT_TERM_LIABILITY: 'text-red-600',
    LONG_TERM_LIABILITY: 'text-red-700',
    EQUITY: 'text-purple-600',
    INCOME: 'text-green-600',
    COST: 'text-orange-600',
  };

  const num = (n: number) => (Math.abs(n) < 0.005 ? '-' : formatCurrency(n, 'TRY'));

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="space-y-4 rounded-lg border bg-card p-4">
        <MizanPeriodPicker value={period} onChange={setPeriod} ns="tradingAccounting" keyPrefix="reports" />
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <Label>{t('reports.accountCodeFrom', 'Account Code From')}</Label>
            <Input
              type="text"
              value={codeFrom}
              onChange={(e) => setCodeFrom(e.target.value)}
              placeholder="100"
              className="w-36"
            />
          </div>
          <div className="space-y-2">
            <Label>{t('reports.accountCodeTo', 'Account Code To')}</Label>
            <Input
              type="text"
              value={codeTo}
              onChange={(e) => setCodeTo(e.target.value)}
              placeholder="799"
              className="w-36"
            />
          </div>
          <div className="space-y-2">
            <Label>{t('reports.level', 'Detail Level')}</Label>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className="block h-10 w-48 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">{t('reports.levelDetail', 'Detailed (all accounts)')}</option>
              <option value="1">{t('reports.level1', 'Level 1 (main accounts)')}</option>
              <option value="2">{t('reports.level2', 'Level 2')}</option>
              <option value="3">{t('reports.level3', 'Level 3')}</option>
            </select>
          </div>
          <div className="flex items-center gap-2 pb-2.5">
            <input
              type="checkbox"
              id="includeZero"
              checked={includeZero}
              onChange={(e) => setIncludeZero(e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="includeZero">{t('reports.includeZeroBalances', 'Include zero balances')}</Label>
          </div>
          <div className="ml-auto pb-1">
            <Button variant="outline" onClick={onExport} disabled={exporting || !data?.accounts.length}>
              {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              {t('reports.export', 'Export')}
            </Button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : data ? (
        <div className="rounded-xl border bg-card">
          <div className="p-4 border-b flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">{t('reports.trialBalance', 'Trial Balance (Mizan)')}</h3>
              <p className="text-sm text-muted-foreground">
                {data.startDate
                  ? `${new Date(data.startDate).toLocaleDateString(dateLocale)} - ${new Date(data.endDate).toLocaleDateString(dateLocale)}`
                  : t('reports.asOfValue', 'As of {{date}}', {
                      date: new Date(data.endDate).toLocaleDateString(dateLocale),
                    })}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {data.totals.isBalanced ? (
                <span className="flex items-center gap-1 text-green-600 text-sm">
                  <CheckCircle className="h-4 w-4" />
                  {t('reports.balanced', 'Balanced')}
                </span>
              ) : (
                <span className="flex items-center gap-1 text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {t('reports.outOfBalance', 'Out of Balance')}
                </span>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('reports.code', 'Code')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('reports.accountName', 'Account Name')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('reports.type', 'Type')}</th>
                  {data.hasOpening && (
                    <>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('reports.openingDebit', 'Devir Borç')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('reports.openingCredit', 'Devir Alacak')}</th>
                    </>
                  )}
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('reports.debit', 'Debit (Borç)')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('reports.credit', 'Credit (Alacak)')}</th>
                  {data.hasOpening && (
                    <>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('reports.totalDebit', 'Toplam Borç')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('reports.totalCredit', 'Toplam Alacak')}</th>
                    </>
                  )}
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('reports.debitBalance', 'Debit Balance')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('reports.creditBalance', 'Credit Balance')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.accounts.map((account) => (
                  <tr key={account.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 font-mono text-sm">{account.code}</td>
                    <td className="px-4 py-3">{account.name}</td>
                    <td className={`px-4 py-3 text-sm ${typeColors[account.type] || ''}`}>
                      {TDMS_TYPE_LABELS[account.type] || account.type}
                    </td>
                    {data.hasOpening && (
                      <>
                        <td className="px-4 py-3 text-right font-mono">{num(account.openingDebit)}</td>
                        <td className="px-4 py-3 text-right font-mono">{num(account.openingCredit)}</td>
                      </>
                    )}
                    <td className="px-4 py-3 text-right font-mono">{num(account.debitTurnover)}</td>
                    <td className="px-4 py-3 text-right font-mono">{num(account.creditTurnover)}</td>
                    {data.hasOpening && (
                      <>
                        <td className="px-4 py-3 text-right font-mono">{num(account.totalDebit)}</td>
                        <td className="px-4 py-3 text-right font-mono">{num(account.totalCredit)}</td>
                      </>
                    )}
                    <td className="px-4 py-3 text-right font-mono">{num(account.debitBalance)}</td>
                    <td className="px-4 py-3 text-right font-mono">{num(account.creditBalance)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/30 font-bold">
                <tr>
                  <td className="px-4 py-3" colSpan={3}>{t('reports.totals', 'Totals')}</td>
                  {data.hasOpening && (
                    <>
                      <td className="px-4 py-3 text-right font-mono">{formatCurrency(data.totals.openingDebit, 'TRY')}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatCurrency(data.totals.openingCredit, 'TRY')}</td>
                    </>
                  )}
                  <td className="px-4 py-3 text-right font-mono">
                    {formatCurrency(data.totals.debitTurnover, 'TRY')}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {formatCurrency(data.totals.creditTurnover, 'TRY')}
                  </td>
                  {data.hasOpening && (
                    <>
                      <td className="px-4 py-3 text-right font-mono">{formatCurrency(data.totals.totalDebit, 'TRY')}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatCurrency(data.totals.totalCredit, 'TRY')}</td>
                    </>
                  )}
                  <td className="px-4 py-3 text-right font-mono">
                    {formatCurrency(data.totals.debitBalance, 'TRY')}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {formatCurrency(data.totals.creditBalance, 'TRY')}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Profit & Loss Report (Gelir Tablosu)
function ProfitLossReport() {
  const { t, i18n } = useTranslation('tradingAccounting');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1);

  const [startDate, setStartDate] = useState(`${today.getFullYear()}-01-01`);
  const [endDate, setEndDate] = useState(new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul' }).format(today));

  const { data, isLoading } = trpc.tradingFinancialReports.getProfitAndLoss.useQuery({
    startDate: new Date(startDate || startOfYear),
    endDate: new Date(endDate || today),
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-end gap-4 rounded-lg border bg-card p-4">
        <div className="space-y-2">
          <Label>{t('reports.startDate', 'Start Date')}</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-48"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('reports.endDate', 'End Date')}</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-48"
          />
        </div>
      </div>

      {data && (
        <ReportExportButton onExport={() => downloadXlsx([...data.income.accounts.map((a) => ({ Bölüm: 'GELİR', Kod: a.code, 'Hesap Adı': a.name, Tutar: a.amount })), { Bölüm: 'GELİR', Kod: '', 'Hesap Adı': 'TOPLAM GELİR', Tutar: data.income.total }, ...data.costs.accounts.map((a) => ({ Bölüm: 'MALİYET / GİDER', Kod: a.code, 'Hesap Adı': a.name, Tutar: a.amount })), { Bölüm: 'MALİYET / GİDER', Kod: '', 'Hesap Adı': 'TOPLAM', Tutar: data.costs.total }, { Bölüm: '', Kod: '', 'Hesap Adı': 'NET KÂR / ZARAR', Tutar: data.netIncome }], 'Gelir Tablosu', `gelir-tablosu-${dateStamp()}.xlsx`)} />
      )}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : data ? (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Income Section */}
          <div className="rounded-xl border bg-card">
            <div className="p-4 border-b bg-green-50">
              <h3 className="text-lg font-semibold text-green-700">{t('reports.income', 'Income (Gelirler)')}</h3>
            </div>
            <div className="divide-y">
              {data.income.accounts.length === 0 ? (
                <p className="p-4 text-muted-foreground text-sm">{t('reports.noIncome', 'No income recorded')}</p>
              ) : (
                data.income.accounts.map((account) => (
                  <div key={account.id} className="flex justify-between p-4">
                    <div>
                      <span className="font-mono text-sm text-muted-foreground">{account.code}</span>
                      <span className="ml-2">{account.name}</span>
                    </div>
                    <span className="font-mono">{formatCurrency(account.amount, 'TRY')}</span>
                  </div>
                ))
              )}
            </div>
            <div className="p-4 border-t bg-green-50 flex justify-between font-bold">
              <span>{t('reports.totalIncome', 'Total Income')}</span>
              <span className="font-mono text-green-700">
                {formatCurrency(data.income.total, 'TRY')}
              </span>
            </div>
          </div>

          {/* Costs Section */}
          <div className="rounded-xl border bg-card">
            <div className="p-4 border-b bg-orange-50">
              <h3 className="text-lg font-semibold text-orange-700">{t('reports.costs', 'Costs (Giderler)')}</h3>
            </div>
            <div className="divide-y">
              {data.costs.accounts.length === 0 ? (
                <p className="p-4 text-muted-foreground text-sm">{t('reports.noCosts', 'No costs recorded')}</p>
              ) : (
                data.costs.accounts.map((account) => (
                  <div key={account.id} className="flex justify-between p-4">
                    <div>
                      <span className="font-mono text-sm text-muted-foreground">{account.code}</span>
                      <span className="ml-2">{account.name}</span>
                    </div>
                    <span className="font-mono">{formatCurrency(account.amount, 'TRY')}</span>
                  </div>
                ))
              )}
            </div>
            <div className="p-4 border-t bg-orange-50 flex justify-between font-bold">
              <span>{t('reports.totalCosts', 'Total Costs')}</span>
              <span className="font-mono text-orange-700">
                {formatCurrency(data.costs.total, 'TRY')}
              </span>
            </div>
          </div>

          {/* Net Income */}
          <div className="lg:col-span-2">
            <div className={`rounded-xl border p-6 ${data.isProfitable ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold">{data.isProfitable ? t('reports.netIncome', 'Net Income') : t('reports.netLoss', 'Net Loss')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {startDate ? new Date(startDate).toLocaleDateString(dateLocale) : ''} - {endDate ? new Date(endDate).toLocaleDateString(dateLocale) : ''}
                  </p>
                </div>
                <span className={`text-3xl font-bold font-mono ${data.isProfitable ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCurrency(Math.abs(data.netIncome), 'TRY')}
                </span>
              </div>
              {data.profitMargin !== undefined && (
                <p className="mt-2 text-sm text-muted-foreground">
                  {t('reports.profitMargin', 'Profit Margin')}: {data.profitMargin.toFixed(2)}%
                </p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Balance Sheet Report (Bilanco)
function BalanceSheetReport() {
  const { t } = useTranslation('tradingAccounting');
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0] ?? '');

  const { data, isLoading } = trpc.tradingFinancialReports.getBalanceSheet.useQuery({
    asOfDate: new Date(asOfDate || new Date()),
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-end gap-4 rounded-lg border bg-card p-4">
        <div className="space-y-2">
          <Label>{t('reports.asOfDate', 'As of Date')}</Label>
          <Input
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            className="w-48"
          />
        </div>
      </div>

      {data && (
        <ReportExportButton onExport={() => downloadXlsx([...data.assets.currentAssets.accounts.map((a) => ({ Bölüm: 'DÖNEN VARLIKLAR', Kod: a.code, 'Hesap Adı': a.name, Bakiye: a.balance })), { Bölüm: 'DÖNEN VARLIKLAR', Kod: '', 'Hesap Adı': 'TOPLAM', Bakiye: data.assets.currentAssets.total }, ...data.assets.fixedAssets.accounts.map((a) => ({ Bölüm: 'DURAN VARLIKLAR', Kod: a.code, 'Hesap Adı': a.name, Bakiye: a.balance })), { Bölüm: 'DURAN VARLIKLAR', Kod: '', 'Hesap Adı': 'TOPLAM', Bakiye: data.assets.fixedAssets.total }, { Bölüm: 'AKTİF', Kod: '', 'Hesap Adı': 'AKTİF TOPLAMI', Bakiye: data.assets.total }, ...data.liabilities.shortTerm.accounts.map((a) => ({ Bölüm: 'KISA VADELİ YABANCI KAYNAKLAR', Kod: a.code, 'Hesap Adı': a.name, Bakiye: a.balance })), { Bölüm: 'KISA VADELİ YABANCI KAYNAKLAR', Kod: '', 'Hesap Adı': 'TOPLAM', Bakiye: data.liabilities.shortTerm.total }, ...data.liabilities.longTerm.accounts.map((a) => ({ Bölüm: 'UZUN VADELİ YABANCI KAYNAKLAR', Kod: a.code, 'Hesap Adı': a.name, Bakiye: a.balance })), { Bölüm: 'UZUN VADELİ YABANCI KAYNAKLAR', Kod: '', 'Hesap Adı': 'TOPLAM', Bakiye: data.liabilities.longTerm.total }, ...data.equity.accounts.map((a) => ({ Bölüm: 'ÖZKAYNAKLAR', Kod: a.code, 'Hesap Adı': a.name, Bakiye: a.balance })), { Bölüm: 'ÖZKAYNAKLAR', Kod: '', 'Hesap Adı': 'DÖNEM KÂRI / ZARARI', Bakiye: data.equity.retainedEarnings }, { Bölüm: 'ÖZKAYNAKLAR', Kod: '', 'Hesap Adı': 'TOPLAM', Bakiye: data.equity.total }, { Bölüm: '', Kod: '', 'Hesap Adı': 'PASİF TOPLAMI', Bakiye: data.totalLiabilitiesAndEquity }], 'Bilanço', `bilanco-${dateStamp()}.xlsx`)} />
      )}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Assets */}
            <div className="rounded-xl border bg-card">
              <div className="p-4 border-b bg-blue-50">
                <h3 className="text-lg font-semibold text-blue-700">{t('reports.assets', 'Assets (Varliklar)')}</h3>
              </div>

              {/* Current Assets */}
              <div className="p-4 border-b">
                <h4 className="font-medium text-blue-600 mb-2">{TDMS_TYPE_LABELS.CURRENT_ASSET}</h4>
                {data.assets.currentAssets.accounts.length === 0 ? (
                  <p className="text-muted-foreground text-sm">{t('reports.noCurrentAssets', 'No current assets')}</p>
                ) : (
                  <div className="space-y-2">
                    {data.assets.currentAssets.accounts.map((account) => (
                      <div key={account.id} className="flex justify-between">
                        <div>
                          <span className="font-mono text-sm text-muted-foreground">{account.code}</span>
                          <span className="ml-2 text-sm">{account.name}</span>
                        </div>
                        <span className="font-mono text-sm">{formatCurrency(account.balance, 'TRY')}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-between mt-2 pt-2 border-t font-medium">
                  <span>{t('reports.subtotal', 'Subtotal')}</span>
                  <span className="font-mono">{formatCurrency(data.assets.currentAssets.total, 'TRY')}</span>
                </div>
              </div>

              {/* Fixed Assets */}
              <div className="p-4">
                <h4 className="font-medium text-blue-600 mb-2">{TDMS_TYPE_LABELS.FIXED_ASSET}</h4>
                {data.assets.fixedAssets.accounts.length === 0 ? (
                  <p className="text-muted-foreground text-sm">{t('reports.noFixedAssets', 'No fixed assets')}</p>
                ) : (
                  <div className="space-y-2">
                    {data.assets.fixedAssets.accounts.map((account) => (
                      <div key={account.id} className="flex justify-between">
                        <div>
                          <span className="font-mono text-sm text-muted-foreground">{account.code}</span>
                          <span className="ml-2 text-sm">{account.name}</span>
                        </div>
                        <span className="font-mono text-sm">{formatCurrency(account.balance, 'TRY')}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-between mt-2 pt-2 border-t font-medium">
                  <span>{t('reports.subtotal', 'Subtotal')}</span>
                  <span className="font-mono">{formatCurrency(data.assets.fixedAssets.total, 'TRY')}</span>
                </div>
              </div>

              <div className="p-4 border-t bg-blue-50 flex justify-between font-bold">
                <span>{t('reports.totalAssets', 'Total Assets')}</span>
                <span className="font-mono text-blue-700">
                  {formatCurrency(data.assets.total, 'TRY')}
                </span>
              </div>
            </div>

            {/* Liabilities & Equity */}
            <div className="space-y-6">
              {/* Liabilities */}
              <div className="rounded-xl border bg-card">
                <div className="p-4 border-b bg-red-50">
                  <h3 className="text-lg font-semibold text-red-700">{t('reports.liabilities', 'Liabilities (Yabanci Kaynaklar)')}</h3>
                </div>

                {/* Short-term */}
                <div className="p-4 border-b">
                  <h4 className="font-medium text-red-600 mb-2">{TDMS_TYPE_LABELS.SHORT_TERM_LIABILITY}</h4>
                  {data.liabilities.shortTerm.accounts.length === 0 ? (
                    <p className="text-muted-foreground text-sm">{t('reports.noShortTermLiabilities', 'No short-term liabilities')}</p>
                  ) : (
                    <div className="space-y-2">
                      {data.liabilities.shortTerm.accounts.map((account) => (
                        <div key={account.id} className="flex justify-between">
                          <div>
                            <span className="font-mono text-sm text-muted-foreground">{account.code}</span>
                            <span className="ml-2 text-sm">{account.name}</span>
                          </div>
                          <span className="font-mono text-sm">{formatCurrency(account.balance, 'TRY')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-between mt-2 pt-2 border-t font-medium">
                    <span>{t('reports.subtotal', 'Subtotal')}</span>
                    <span className="font-mono">{formatCurrency(data.liabilities.shortTerm.total, 'TRY')}</span>
                  </div>
                </div>

                {/* Long-term */}
                <div className="p-4">
                  <h4 className="font-medium text-red-600 mb-2">{TDMS_TYPE_LABELS.LONG_TERM_LIABILITY}</h4>
                  {data.liabilities.longTerm.accounts.length === 0 ? (
                    <p className="text-muted-foreground text-sm">{t('reports.noLongTermLiabilities', 'No long-term liabilities')}</p>
                  ) : (
                    <div className="space-y-2">
                      {data.liabilities.longTerm.accounts.map((account) => (
                        <div key={account.id} className="flex justify-between">
                          <div>
                            <span className="font-mono text-sm text-muted-foreground">{account.code}</span>
                            <span className="ml-2 text-sm">{account.name}</span>
                          </div>
                          <span className="font-mono text-sm">{formatCurrency(account.balance, 'TRY')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-between mt-2 pt-2 border-t font-medium">
                    <span>{t('reports.subtotal', 'Subtotal')}</span>
                    <span className="font-mono">{formatCurrency(data.liabilities.longTerm.total, 'TRY')}</span>
                  </div>
                </div>

                <div className="p-4 border-t bg-red-50 flex justify-between font-bold">
                  <span>{t('reports.totalLiabilities', 'Total Liabilities')}</span>
                  <span className="font-mono text-red-700">
                    {formatCurrency(data.liabilities.total, 'TRY')}
                  </span>
                </div>
              </div>

              {/* Equity */}
              <div className="rounded-xl border bg-card">
                <div className="p-4 border-b bg-purple-50">
                  <h3 className="text-lg font-semibold text-purple-700">{t('reports.equity', 'Equity (Oz Kaynaklar)')}</h3>
                </div>
                <div className="divide-y">
                  {data.equity.accounts.map((account) => (
                    <div key={account.id} className="flex justify-between p-4">
                      <div>
                        <span className="font-mono text-sm text-muted-foreground">{account.code}</span>
                        <span className="ml-2">{account.name}</span>
                      </div>
                      <span className="font-mono">{formatCurrency(account.balance, 'TRY')}</span>
                    </div>
                  ))}
                  <div className="flex justify-between p-4 bg-muted/30">
                    <span className="italic">{t('reports.retainedEarnings', 'Retained Earnings')}</span>
                    <span className="font-mono">{formatCurrency(data.equity.retainedEarnings, 'TRY')}</span>
                  </div>
                </div>
                <div className="p-4 border-t bg-purple-50 flex justify-between font-bold">
                  <span>{t('reports.totalEquity', 'Total Equity')}</span>
                  <span className="font-mono text-purple-700">
                    {formatCurrency(data.equity.total, 'TRY')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Balance Check */}
          <div className={`rounded-xl border p-6 ${data.isBalanced ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-muted-foreground">{t('reports.totalAssets', 'Total Assets')}</p>
                <p className="text-xl font-bold font-mono">{formatCurrency(data.assets.total, 'TRY')}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">=</p>
                <p className="text-xl font-bold">=</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('reports.liabilitiesPlusEquity', 'Liabilities + Equity')}</p>
                <p className="text-xl font-bold font-mono">{formatCurrency(data.totalLiabilitiesAndEquity, 'TRY')}</p>
              </div>
            </div>
            <div className="text-center mt-4">
              {data.isBalanced ? (
                <span className="flex items-center justify-center gap-2 text-green-700">
                  <CheckCircle className="h-5 w-5" />
                  {t('reports.balanceSheetBalanced', 'Balance Sheet is balanced')}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  {t('reports.balanceSheetNotBalanced', 'Balance Sheet is out of balance by')} {formatCurrency(data.difference, 'TRY')}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
